import type { RegisteredEditorModule } from '../../types';
import {
  createSudokuGame,
  duplicateSudoku,
  getSudokuSource,
  previewSudoku,
  replaceSudoku,
  updateJogo,
  TIPO_JOGO,
} from '../../../../../services/admin';
import { createEmpty, type SudokuDoc } from './doc';
import { sudokuReducer, type SudokuAction } from './reducer';
import { validate } from './validate';
import { coalesceKeyFor, initialSudokuUi } from './ui';
import { blobToDoc, docToBlob } from './wire';
import { Canvas } from './Canvas';
import { Inspector } from './Inspector';

// Monta o EditorModule do sudoku: núcleo puro + serviços (via wire) + UI. O shell só
// conhece esta abstração — ele não aprende que sudoku tem grade, nem que o blob dele
// trafega como string.

const payloadFrom = (doc: SudokuDoc) => ({ titulo: doc.titulo, game: docToBlob(doc) });

// Sudoku não tem numeração derivada (não há `deriveIndices` — ver abaixo), então
// `indices` é sempre vazio.
const NO_INDICES: Record<string, number> = {};

export const sudokuModule: RegisteredEditorModule<SudokuDoc, SudokuAction> = {
  kind: 'sudoku',
  label: 'Sudoku',

  // Sudoku é JOGO (não passatempo): quem oferece o "Editar grade" dele é o catálogo
  // de Jogos, casando pelo `idTipo`. Declarar isto é tudo o que o módulo precisa
  // fazer para aparecer lá — nenhum `if` de tipo no catálogo.
  entry: { catalog: 'jogo', idTipo: TIPO_JOGO.SUDOKU },

  newPuzzle: {
    title: 'Novo sudoku',
    description:
      'Um sudoku 9×9 novo — já nasce sorteado; o ajuste é ligar e desligar as casas que vêm preenchidas.',
    titlePlaceholder: 'Ex.: Sudoku de domingo',
    // O tamanho é do TIPO, não do autor: 9 × 9 e ponto (§2). O modal mostra os
    // campos travados, e o `createEmpty` ignora o que vier neles.
    dimensions: { mode: 'fixed', largura: 9, altura: 9 },
    // Sem `tipo`: o `id_tipo` do jogo é fixado pelo endpoint de criação
    // (`createSudokuGame`), então não há o que o autor escolher aqui.
  },

  createEmpty,
  initialUi: initialSudokuUi,
  reduce: sudokuReducer,
  validate,
  coalesceKey: coalesceKeyFor,

  load: (id) =>
    getSudokuSource(id).then((src) => {
      // O `ShowSudokuServiceImpl` do backend devolve `null` quando o jogo existe mas
      // o tabuleiro não foi gravado — o mesmo órfão que `createSudokuGame` (em
      // services/admin.ts) evita na criação, só que já consumado (o `create_jogo`
      // passou, o `create_sudoku` falhou depois). Sem esta guarda, `src.game`
      // estoura em TypeError; com ela, o autor recebe uma mensagem legível.
      if (!src) {
        throw new Error(
          `O jogo #${id} não tem tabuleiro de sudoku gravado — provavelmente ficou órfão ` +
            '(o jogo foi criado, mas a etapa que grava a grade falhou depois). Exclua o ' +
            `jogo #${id} no catálogo de Jogos e crie um novo.`,
        );
      }
      return {
        // O show_sudoku não informa título nem ranking. Título vazio aqui: o autor
        // completa no Inspector. Ranking não informado → `editavel: true`; quem barra
        // de verdade uma edição indevida é o 409 do servidor no save.
        doc: blobToDoc(src.game, ''),
        editavel: true,
      };
    }),

  save: (id, doc) => {
    const payload = payloadFrom(doc);
    if (id != null) {
      // O PUT de /admin/jogos/:id/sudoku só aceita `game` — o endpoint não tem campo
      // `titulo`. Por isso o título, quando preenchido, vai numa segunda chamada ao
      // endpoint genérico de jogo. Guard vazio: logo após o load o campo vem ''
      // (show_sudoku não devolve título) e salvar nesse estado não pode apagar o
      // título real que já está gravado no jogo.
      return replaceSudoku(id, payload).then(() => {
        if (doc.titulo.trim() === '') return { id, indices: NO_INDICES };
        return updateJogo(id, { titulo: doc.titulo.trim() }).then(
          () => ({ id, indices: NO_INDICES }),
          (err: unknown) => {
            // O tabuleiro já foi gravado; só o título falhou. O erro sobe (o autor
            // pediu para salvar um título e isso não aconteceu), mas a mensagem
            // registra que a grade está segura.
            const detail = err instanceof Error ? err.message : String(err);
            throw new Error(
              `O tabuleiro foi salvo, mas o título não pôde ser atualizado: ${detail}. ` +
                'Tente salvar novamente para reenviar o título.',
            );
          },
        );
      });
    }
    // createSudokuGame cria em dois passos (create_jogo e depois create_sudoku).
    // Quando o segundo falha, a mensagem que vem de lá já diz qual jogo ficou órfão e
    // que ele precisa ser excluído (ver services/admin.ts); aqui a mensagem só ganha
    // o contexto da ação.
    return createSudokuGame(payload).then(
      (r) => ({ id: r.id, indices: NO_INDICES }),
      (err: unknown) => {
        const detail = err instanceof Error ? err.message : String(err);
        throw new Error(
          'Falha ao criar o sudoku. Se o jogo já tiver sido criado no servidor (a criação ' +
            'acontece em duas etapas), tentar salvar de novo agora criaria outro registro ' +
            `órfão. Confira com o time antes de tentar de novo. Detalhe: ${detail}`,
        );
      },
    );
  },

  preview: (doc) => previewSudoku(payloadFrom(doc)).then(() => ({ id: 0, indices: NO_INDICES })),

  // `deriveIndices` AUSENTE de propósito (§8): sudoku não tem numeração derivada,
  // logo não há drift para o shell conferir. O `preview` continua batendo no
  // servidor — é o botão "Prévia" do EditorShell.

  // Rota PRÓPRIA do sudoku. O `duplicateJogo` genérico é servido pelo controller do
  // caça-palavras e tem guard de tipo: usá-lo aqui devolveria 404.
  duplicate: duplicateSudoku,

  import: {
    kind: 'sudoku',
    toDoc: (result) => {
      if (result.kind !== 'sudoku') throw new Error(`Import de ${result.kind} no sudoku.`);
      return blobToDoc(result.game, result.titulo);
    },
  },

  Canvas,
  Inspector,
};
